// 2026-09-28: fájl megnyitása / letöltése / előnézete az AKTÍV CÉG nevében.
//
// ⚠⚠ Miért nem sima `<a href="/api/...">` vagy `<img src="/api/...">`: a böngésző
// navigációja (és az <img> betöltése) NEM viszi az `X-Company-Id` fejlécet — azt csak
// az axios-példány interceptora teszi fel (services/api.js). Fejléc nélkül a backend a
// legrégebbi tagság cégére esik vissza (`_resolve_company_id`), ott a bizonylat nincs,
// és a válasz `404 "Bizonylat nem található"`. Élő hiba 2026-09-28: a PhenoGyde a 2-es
// cég lett, a legrégebbi tagság a TESZT CÉG-é → a PhenoGyde egyetlen PDF-je sem nyílt.
// Ezért itt MINDEN fájl az axios-kliensen megy (fejléccel), és blob-URL-ként nyílik.
// Új, fájlt kiszolgáló végpontra mutató link is EZEN menjen.
import { useEffect, useState } from 'react';
import { api } from '../services/api';

// A `/api/...` alakú URL-ekből az axios baseURL-hez (`/api`) relatív út.
const rel = (url) => (url.startsWith('/api/') ? url.slice(4) : url);

// A blob-URL-t nem vonjuk vissza azonnal: az új lap még tölti / a PDF-néző újraolvashatja.
const REVOKE_AFTER_MS = 5 * 60 * 1000;

// `Blob.text()` nem mindenhol van (régebbi böngésző, jsdom) — FileReader a tartalék.
function blobText(blob) {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(fr.error);
    fr.readAsText(blob);
  });
}

async function errorText(err) {
  const data = err?.response?.data;
  let detail = null;
  if (data instanceof Blob) {
    try { detail = JSON.parse(await blobText(data))?.detail; } catch { /* nem JSON */ }
  } else if (data && typeof data === 'object') {
    detail = data.detail;
  }
  const status = err?.response?.status;
  if (typeof detail !== 'string') detail = null;
  return detail || (status ? `A fájl nem tölthető le (HTTP ${status}).` : 'A fájl nem tölthető le.');
}

async function fetchBlob(url) {
  const r = await api.get(rel(url), { responseType: 'blob' });
  return r;
}

/** Megnyitás új lapon. A lapot a kattintás pillanatában (szinkron) nyitjuk, különben a
 *  böngésző felugró ablakként blokkolná; a tartalom a letöltés után kerül bele. */
export async function openAuthedFile(url) {
  const win = window.open('', '_blank');
  if (win) {
    try { win.opener = null; } catch { /* no-op */ }
    try {
      win.document.title = 'Betöltés…';
      win.document.body.style.cssText = 'font-family:sans-serif;color:#555;padding:2rem';
      win.document.body.textContent = 'Fájl betöltése…';
    } catch { /* no-op */ }
  }
  try {
    const r = await fetchBlob(url);
    const href = URL.createObjectURL(r.data);
    if (win) win.location.href = href;
    else window.location.href = href;   // blokkolt felugró ablak: ugyanebben a lapban
    setTimeout(() => URL.revokeObjectURL(href), REVOKE_AFTER_MS);
  } catch (err) {
    const msg = await errorText(err);
    if (win) {
      try { win.document.title = 'Hiba'; win.document.body.textContent = msg; } catch { /* no-op */ }
    } else {
      window.alert(msg);
    }
  }
}

/** Letöltés fájlként (a szerver Content-Disposition nevével, ha van). */
export async function downloadAuthedFile(url, fallbackName) {
  try {
    const r = await fetchBlob(url);
    const cd = r.headers?.['content-disposition'] || '';
    const m = /filename\*=UTF-8''([^;]+)/i.exec(cd) || /filename="?([^";]+)"?/i.exec(cd);
    const name = m ? decodeURIComponent(m[1]) : fallbackName;
    const href = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = href; a.download = name || 'letoltes';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(href), REVOKE_AFTER_MS);
  } catch (err) {
    window.alert(await errorText(err));
  }
}

/** `<a>`, ami az aktív cég nevében nyitja meg a fájlt. A `href` a hover-előnézetnek
 *  marad, de a kattintást (a Ctrl/középső kattintást is) mi kezeljük: a böngésző
 *  saját navigációja fejléc nélkül menne. */
export function AuthedLink({ href, children, onClick, target, rel: _rel, ...rest }) {
  // Külső cím (pl. a régi SharePoint webUrl) — ott nincs mit hozzátenni, sima link.
  if (!href || !href.startsWith('/api/')) {
    return <a href={href} target="_blank" rel="noreferrer" onClick={onClick} {...rest}>{children}</a>;
  }
  const handle = (e) => {
    if (e.type === 'auxclick' && e.button !== 1) return;
    e.preventDefault();
    e.stopPropagation();
    onClick?.(e);
    openAuthedFile(href);
  };
  return (
    <a href={href} onClick={handle} onAuxClick={handle} {...rest}>{children}</a>
  );
}

/** Blob-URL egy képhez (bélyegkép-előnézet), az aktív cég nevében betöltve.
 *  `{ src, error }` — `src` null, amíg tölt vagy ha hiba volt. */
export function useAuthedObjectUrl(url) {
  const [state, setState] = useState({ src: null, error: false });
  useEffect(() => {
    if (!url) { setState({ src: null, error: false }); return undefined; }
    let alive = true;
    let href = null;
    setState({ src: null, error: false });
    fetchBlob(url)
      .then((r) => {
        href = URL.createObjectURL(r.data);
        if (alive) setState({ src: href, error: false });
        else URL.revokeObjectURL(href);
      })
      .catch(() => { if (alive) setState({ src: null, error: true }); });
    return () => { alive = false; if (href) URL.revokeObjectURL(href); };
  }, [url]);
  return state;
}
